import { Mui, MuiIcons, MuiStyles } from '@osu/react-ui';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from "react-router-dom";
import {
  getCommitteeReviewersState,
  getStudentState,
  getTrainingState,
} from '../../../../Common/redux/selectors';
import { createTraining as createTrainingAction } from '../../../../services/graphql/slices/applicantActionsSlice';

const IOSSwitch = MuiStyles.styled((props) => (
  <Mui.Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  margin: theme.spacing(1),
  '& .MuiSwitch-switchBase': {
    padding: 1,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: theme.palette.common.white,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette?.colors?.blue || theme.palette?.primary?.main || '#1976d2',
        opacity: 1,
        border: 'none',
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#52d869',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    width: 24,
    height: 24,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
}));

const CreateTrainingDialogContent = ({ isTraining }) => {
  const dispatch = useDispatch();
  const trainingState = useSelector((state) => getTrainingState(state));
  
  const studentState = useSelector((state) => getStudentState(state));
  const location = useLocation();
  const {
    emplids,
    expectedRecommendation,
    expJunior,
    expIntermediate,
    expAdvanced,
    expSenior,
    jobTypeRegular,
    jobTypeSeasonal,
    allReviewers,
  } = trainingState;
  
  const reviewersFromDB = useSelector(
    (state) => getCommitteeReviewersState(state)?.data ?? []
  );
 
  let filteredReviewersFromDB = reviewersFromDB;
  
  // Only filter when assigning on the training page
  if (isTraining) {
    const attachedReviewerIds = studentState?.data?.readers.map((item) => item.readerEmployeeId) ?? [];
    let unassignedReviewers = studentState?.data?.readers?.filter((item) => item.status.includes('UNASSIGNED'))
    unassignedReviewers = unassignedReviewers?.map((item) => item.readerEmployeeId);
    filteredReviewersFromDB = reviewersFromDB.filter((reviewer) => unassignedReviewers?.includes(reviewer.emplid) || !attachedReviewerIds.includes(reviewer.emplid));
  }
  
  
  const committeeButtonMap = trainingState?.recommendationOptions ?? [];

  const transformedReviewers = filteredReviewersFromDB?.map((item) => 
  ({
    campusId: item.campusId,
    emplid: item.emplid,
    experienceLabel: item.experienceLabel ? item.experienceLabel : "Junior",
    firstName: item.firstName,
    jobType: item.jobType ? item.jobType : "Seasonal",
    lastName: item.lastName
  }));
 
  const reviewersAvailable = transformedReviewers.filter(
    (reviewer) =>
      (
      (expJunior && (reviewer?.experienceLabel === 'Junior' || reviewer?.experienceLabel === null)) ||
      (expIntermediate && (reviewer?.experienceLabel === 'Intermediate' || reviewer?.experienceLabel === null)) ||
      (expAdvanced && (reviewer?.experienceLabel === 'Advanced' || reviewer?.experienceLabel === null)) ||
      (expSenior && (reviewer?.experienceLabel === 'Senior' || reviewer?.experienceLabel === null))
      ) &&
      (
        (jobTypeSeasonal && (reviewer?.jobType === 'Seasonal' || reviewer?.jobType === null)) ||
      (jobTypeRegular && (reviewer?.jobType === 'Regular' || reviewer?.jobType === null))
      )
  );

  useEffect(() => { // Auto-remove emplids for unchecked filteres
    const selectedEmplids = emplids.filter(emplid => reviewersAvailable.map(e => e.emplid).includes(emplid));
    dispatch(createTrainingAction({ ...trainingState, emplids: selectedEmplids }));
  }, [emplids.length, reviewersAvailable.length]);
 
  const handleChangeIncludeAll = (event) => {
    if (event.target.checked) {
      dispatch(createTrainingAction({
        ...trainingState,
        expJunior: true,
        expIntermediate: true,
        expAdvanced: true,
        expSenior: true,
        jobTypeSeasonal: true,
        jobTypeRegular: true,
        allReviewers: true,
        emplids: [],
      }));
    } else {
      dispatch(createTrainingAction({
        ...trainingState,
        allReviewers: false,
      }));
    }
  };

  const handleChangeCheckbox = (event) => {
    dispatch(createTrainingAction({
      ...trainingState,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleChangeReviewers = (event, values, reason) => {
    let selectedEmplids = values.map(e => e.emplid);
    
    if (selectedEmplids.includes("all")) {
        if (reason === "selectOption" && emplids.length === reviewersAvailable.length) {
            // Clicked "Select All" while we already have everyone selected? Must be trying to clear all selections
            selectedEmplids = [];
        } else {
            selectedEmplids = reviewersAvailable.map(e => e.emplid);
        }
    }
    selectedEmplids = Array.from(new Set(selectedEmplids)); // De-duplicate

     dispatch(createTrainingAction({
      ...trainingState,
      emplids: selectedEmplids 
    }));
  };

  const handleChangeRecommendation = (value) => {
      const recommendation =
        trainingState?.expectedRecommendation === value ? '' : value;
      dispatch(createTrainingAction({
        ...trainingState,
        expectedRecommendation: recommendation,
      }));
  };

  return (
    <Mui.Box>
      <Mui.FormControlLabel
        control={ <IOSSwitch checked={allReviewers} onChange={handleChangeIncludeAll} name="allReviewers" /> }
        label={`Include all reviewers.`}
      />
      
      <ReviewerSelection showComponent={!allReviewers}
        handleChangeCheckbox={handleChangeCheckbox} expJunior={expJunior} expIntermediate={expIntermediate} expAdvanced={expAdvanced} expSenior={expSenior} jobTypeSeasonal={jobTypeSeasonal} jobTypeRegular={jobTypeRegular}
        allReviewers={allReviewers} emplids={emplids} handleChangeReviewers={handleChangeReviewers} reviewersAvailable={reviewersAvailable}
      />
      
      {location?.pathname.includes('/training/') ? null : 
      <Mui.Box> 
      <Mui.Box>
        <Mui.FormControlLabel
        control={
          <IOSSwitch
            checked={trainingState.expectedRecommendation === "None"}
            onChange={(event) => {
              dispatch(createTrainingAction({
                ...trainingState,  
                expectedRecommendation: event.target.checked ? "None" : "",  
              }));
            }}
          />
        }
        label={`No Recommendation Needed`}
      /> 
      </Mui.Box>
      {trainingState.expectedRecommendation === "None" ? null :
       <Mui.Box>
       <Mui.Typography style={{ marginTop: '1rem' }}>
       Expected Recommendation:
       </Mui.Typography>
         <Mui.ButtonGroup aria-label="outlined button group">
           {committeeButtonMap.map((item, index) => {
           const matchesDecision =
           expectedRecommendation.toLowerCase() === item.value.toLowerCase();
           return (
             <Mui.Button
               key={index}
               color={matchesDecision ? 'secondary' : undefined}
               variant={matchesDecision ? 'contained' : undefined}
               onClick={() => {
                 handleChangeRecommendation(item.value);
               }}
             >
               {item.value}  
             </Mui.Button>
           );
         })}
       </Mui.ButtonGroup>
     </Mui.Box>
      }
     
      </Mui.Box>
      }
     
    </Mui.Box>
  );
};

function ReviewerSelection({
    handleChangeCheckbox, expJunior, expIntermediate, expAdvanced, expSenior, jobTypeSeasonal, jobTypeRegular,
    allReviewers, emplids, handleChangeReviewers, reviewersAvailable
}) {
    reviewersAvailable = [{ firstName: "Select", lastName: "All", emplid: "all" }, ...reviewersAvailable];
    
    function formatName(reviewer) {
        if (!reviewer) { return ""; }
        
        const campusId = reviewer.emplid === "all" ? "" : `(${reviewer.campusId})`;
        return `${reviewer.firstName} ${reviewer.lastName} ${campusId}`;
    }
    
    return (
        <Mui.Collapse in={!allReviewers} timeout="auto" unmountOnExit>
            <Mui.Box style={{ padding: '1rem 0 1rem 0' }}>
                <Mui.Typography>Filter reviewers by skill level (optional):</Mui.Typography>

                <ReviewerGroupCheckbox onChange={handleChangeCheckbox} label="Junior" name="expJunior" value={expJunior} />
                <ReviewerGroupCheckbox onChange={handleChangeCheckbox} label="Intermediate" name="expIntermediate" value={expIntermediate} />
                <ReviewerGroupCheckbox onChange={handleChangeCheckbox} label="Advanced" name="expAdvanced" value={expAdvanced} />
                <ReviewerGroupCheckbox onChange={handleChangeCheckbox} label="Senior" name="expSenior" value={expSenior} />
            </Mui.Box>

            <Mui.Box style={{ padding: '1rem 0 1rem 0' }}>
                <Mui.Typography>Filter reviewers by job type (optional):</Mui.Typography>

                <ReviewerGroupCheckbox onChange={handleChangeCheckbox} label="Seasonal" name="jobTypeSeasonal" value={jobTypeSeasonal} />
                <ReviewerGroupCheckbox onChange={handleChangeCheckbox} label="Regular" name="jobTypeRegular" value={jobTypeRegular} />
            </Mui.Box>

            <Mui.InputLabel id="choose-reviewers"> Choose Reviewers{' '} {emplids?.length > 0 && `( ${emplids?.length} selected )`} </Mui.InputLabel>
            
            <Mui.Autocomplete
                id="choose-reviewers"
                disableCloseOnSelect
                getOptionLabel={formatName}
                getOptionSelected={(option, value) => option.emplid === value.emplid}
                multiple={true}
                options={reviewersAvailable.filter(reviewer => !emplids.includes(reviewer.emplid))}
                value={emplids.map(emplid => reviewersAvailable.find(ra => ra.emplid === emplid))}
                isOptionEqualToValue={(a, b) => b.includes?.(a.emplid)}
                onChange={handleChangeReviewers}
                renderInput={(params) => ( <Mui.TextField {...params} variant="outlined" /> )}
                renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isAllOption = option.emplid === "all";
                    const allAreSelected = emplids.length === reviewersAvailable.length - 1;
                    const isAllAndAllAreSelected = isAllOption && allAreSelected;
                    
                    let SelectionIcon = MuiIcons.CheckBoxOutlineBlank;
                    if (isAllOption && emplids.length > 0) { SelectionIcon = MuiIcons.IndeterminateCheckBox; }
                    if (isAllAndAllAreSelected || selected) { SelectionIcon = MuiIcons.CheckBox; }

                    return (
                        <li key={option.emplid} {...optionProps}>
                            <SelectionIcon fontSize="small" style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }} />
                            
                            {formatName(option)}
                        </li>
                    );
                }}
                style={{ minWidth: 300}}
                slotProps={{ endAdornment: { sx: { top: 'calc(50% - 20px)' } } }}
            />
        </Mui.Collapse>
    );
}

function ReviewerGroupCheckbox({ value, onChange, name, label }) {
    return <Mui.FormControlLabel
        control={ <IOSSwitch checked={value} onChange={onChange} name={name} /> }
        label={label}
    />;
}

export default CreateTrainingDialogContent;
